import React, { FC, useMemo } from 'react';
import { ColumnsType } from 'antd/lib/table';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import produce from 'immer';
import { useMediaQuery } from 'react-responsive';

import { useGetApplication } from '~/controllers/wizard';

import { CheckThinIcon } from '~/img';
import EditButton from '~/components/views/ShareholdersMultiple/EditButton';
import DeleteButton from '~/components/views/ShareholdersMultiple/DeleteButton';
import { Draft } from '~/types/Draft';
import { KsaShareholders } from '~/types/ksa/KsaShareholders';
import { hasAtLeastOneDeclinedField, hasAtLeastOneWarningField } from '~/utils/softDeclinesFields';
import theme from '~/constants/theme';
import { Space, TableCardMobile, TextField } from '~/components';
import Table from '~/components/Table';
import { isEditButtonDisabled } from '~/components/views/ShareholdersMultiple/utils';
import { Application } from '~/types/Application';
import { getBeneficialOwnersFormItemsNamePath } from '~/pages/ksa/application/shareholders/components/beneficialOwners/BeneficialOwnerForm';
import classNames from 'classnames';

import { MinusOutlined } from '@ant-design/icons';
import colors from '~/constants/colors';
import { stepName } from '~/pages/ksa/application/shareholders';

interface Props {
  beneficialOwners: BeneficiaryOwnerRowType[];
}

const BeneficialOwnerTable: FC<Props> = (props) => {
  const { beneficialOwners } = props;
  const { t } = useTranslation('ksaApplication');

  const applicationQuery = useGetApplication();

  const individualColumns: ColumnsType<BeneficiaryOwnerRowType> = useMemo(
    () => [
      {
        title: t('First name'),
        dataIndex: 'firstName',
        key: 'firstName',
      },
      {
        title: t('Last name'),
        dataIndex: 'lastName',
        key: 'lastName',
      },
      {
        title: t('Passport'),
        dataIndex: 'hasPassport',
        key: 'hasPassport',
        render: (hasPassport: boolean) => {
          if (hasPassport) return <CheckThinIcon style={checkIconStyle} />;
          return <MinusOutlined style={checkIconStyle} />;
        },
      },
      {
        title: t('Personal document'),
        dataIndex: 'hasPersonalDocument',
        key: 'hasPersonalDocument',
        render: (hasPersonalDocument: boolean) => {
          if (hasPersonalDocument) return <CheckThinIcon style={checkIconStyle} />;
          return <MinusOutlined style={checkIconStyle} />;
        },
      },
      {
        title: t('Shares, %'),
        dataIndex: 'shares',
        key: 'shares',
      },
      {
        title: '',
        key: 'action',
        // fixed: 'right',
        render: (_, { onEdit, onDelete }) => (
          <div style={actionsStyle}>
            <Space size={16}>
              {onEdit && <EditButton id="beneficialOwnerTable_EditButton" onClick={onEdit} />}
              {onDelete && (
                <DeleteButton
                  id="beneficialOwnerTable_DeleteButton"
                  disabled={isEditButtonDisabled(
                    applicationQuery.data,
                    hasAnyDeclinedField(applicationQuery.data)
                  )}
                  onClick={onDelete}
                />
              )}
            </Space>
          </div>
        ),
      },
    ],
    [applicationQuery.data, t]
  );

  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.sm });

  return (
    <>
      <Space size={24} direction="vertical">
        {!isEmpty(beneficialOwners) && (
          <>
            {!isTablet ? (
              <Table
                id="beneficialOwnersTable"
                columns={individualColumns}
                dataSource={beneficialOwners}
                pagination={pagination}
                rowKey="uid"
                rowClassName={(_, index) =>
                  classNames({
                    'ant-table-row-warning': hasAnyActiveWarning(applicationQuery.data, index),
                  })
                }
              />
            ) : (
              <Space size={12} direction="vertical">
                {beneficialOwners.map((item, index) => (
                  <TableCardMobile
                    key={item.uid}
                    disableDeleteAction={isEditButtonDisabled(
                      applicationQuery.data,
                      hasAnyDeclinedField(applicationQuery.data)
                    )}
                    hasValidationWarnings={hasAnyActiveWarning(applicationQuery.data, index)}
                    onEdit={item.onEdit}
                    onDelete={item.onDelete}
                    fields={getBeneficialOwnerCardContent(item)}
                  />
                ))}
              </Space>
            )}
          </>
        )}
      </Space>
    </>
  );
};

const pagination = { hideOnSinglePage: true };

type BeneficiaryOwnerRowType = {
  uid: string;
  firstName: string | undefined;
  lastName: string | undefined;
  hasPassport: boolean;
  hasPersonalDocument: boolean;
  shares: number | undefined;
  onDelete?: () => void;
  onEdit?: () => void;
};

export const getBeneficialOwnerData = (
  shareholdersDraft: Draft<'shareholders'> | undefined,
  openEdit: (uid: string) => void,
  onFormChange?: (value: KsaShareholders) => void
): BeneficiaryOwnerRowType[] => {
  const shareholders = shareholdersDraft?.shareholders as KsaShareholders;
  return (
    shareholders?.beneficialOwners?.map((item) => {
      return {
        uid: item.uid,
        firstName: item?.personalData?.firstName,
        lastName: item?.personalData?.lastName,
        hasPassport: !isEmpty(item?.passport?.documentNumber),
        hasPersonalDocument: !isEmpty(item?.personalDocument?.idType),
        shares: item?.ownershipShare?.percent,
        onDelete: onFormChange
          ? () =>
              onFormChange(
                produce(shareholdersDraft, (draft) => {
                  const draftShareholders = draft?.shareholders as KsaShareholders;
                  draftShareholders.beneficialOwners = draftShareholders.beneficialOwners?.filter(
                    (a) => a?.uid !== item?.uid
                  );
                })?.shareholders as KsaShareholders
              )
          : undefined,
        onEdit: () => item?.uid && openEdit(item.uid),
      };
    }) || []
  );
};

const getBeneficialOwnerCardContent = (record: BeneficiaryOwnerRowType) => {
  const { firstName, lastName, shares, hasPassport, hasPersonalDocument } = record;
  const t = i18next.getFixedT(null, 'ksaApplication');
  return [
    <TextField key="firstName" label={t('First name')}>
      {firstName}
    </TextField>,
    <TextField key="lastName" label={t('Last name')}>
      {lastName}
    </TextField>,
    <TextField key="shares" label={t('Shares, %')}>
      {shares}
    </TextField>,
    <TextField key="hasPassport" label={t('Passport')}>
      {hasPassport ? (
        <CheckThinIcon style={checkIconStyle} />
      ) : (
        <MinusOutlined style={checkIconStyle} />
      )}
    </TextField>,
    <TextField key="hasPersonalDocument" label={t('Personal Document')}>
      {hasPersonalDocument ? (
        <CheckThinIcon style={checkIconStyle} />
      ) : (
        <MinusOutlined style={checkIconStyle} />
      )}
    </TextField>,
  ];
};

export const hasAnyDeclinedField = (application: Application | undefined) =>
  hasAtLeastOneDeclinedField(application, stepName);

const hasAnyActiveWarning = (application: Application | undefined, index: number) =>
  hasAtLeastOneWarningField(application, getBeneficialOwnersFormItemsNamePath(index).join('.'));

const actionsStyle = { display: 'flex', justifyContent: 'flex-end' };
const checkIconStyle = { color: colors.blue['1100'] };

export default BeneficialOwnerTable;
